import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchUserPicture } from "../../store/thunks/userThunk";

import IncrementButton from "./IncrementButton";
import Search from "./Search";
import ProfilePictureModal from "../../modals/ProfilePictureModal";
import ClinicianDetailsCard from "./ClinicianDetailsCard";

import {
  CalendarIcon,
  UserPlusIcon,
  PhoneIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";

function UserBanner({
  onQuickAddClientClick,
  onScheduleModalClick,
  onResourceScheduleModalClick,
  user,
  onSearchResultClick,
}) {
  const dispatch = useDispatch();
  const [profilePicture, setProfilePicture] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const fetchPicture = async () => {
      const result = await dispatch(fetchUserPicture({ userId: user._id }));
      setProfilePicture(result.payload || "https://via.placeholder.com/150");
    };
    fetchPicture();
  }, [dispatch, user._id]);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = async () => {
    setIsModalOpen(false);
    const result = await dispatch(fetchUserPicture({ userId: user._id }));
    setProfilePicture(result.payload || "https://via.placeholder.com/150");
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm px-4 md:px-6 py-3 md:py-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
        <div className="flex items-center">
          <button
            className="relative group flex items-center justify-center h-12 w-12 md:h-16 md:w-16 overflow-hidden rounded-full ring-1 ring-gray-200 transition-all hover:ring-2 hover:ring-indigo-500"
            onClick={handleOpenModal}>
            <img
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              src={profilePicture}
              alt="Profile"
            />
          </button>

          <div className="ml-3 md:ml-4">
            <ClinicianDetailsCard clinician={user} />
          </div>
        </div>

        <div className="flex-1 order-3 md:order-2">
          <div className="relative border border-gray-300 rounded-sm bg-white px-3 md:px-4 py-3 md:py-4">
            <div className="absolute -top-2 left-3 bg-white px-2 text-xs font-medium text-gray-500">
              Quick Actions
            </div>

            <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
              <div className="w-full md:flex-grow md:min-w-[180px]">
                <Search onResultClick={onSearchResultClick} />
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                {[
                  {
                    icon: <UserPlusIcon className="h-4 w-4 md:h-5 md:w-5" />,
                    onClick: onQuickAddClientClick,
                    label: "Add Client",
                  },
                  !user.role.includes("clinician") && {
                    component: (
                      <IncrementButton
                        icon={<PhoneIcon className="h-4 w-4 md:h-5 md:w-5" />}
                        label="Received Calls"
                        className="h-8 w-8 md:h-9 md:w-9"
                      />
                    ),
                  },
                  {
                    icon: <CalendarIcon className="h-4 w-4 md:h-5 md:w-5" />,
                    onClick: onScheduleModalClick,
                    label: "Daily Schedule",
                  },
                  {
                    icon: (
                      <BuildingOffice2Icon className="h-4 w-4 md:h-5 md:w-5" />
                    ),
                    onClick: onResourceScheduleModalClick,
                    label: "Resource Schedule",
                  },
                ]
                  .filter(Boolean)
                  .map(({ icon, onClick, label, component }, i) => (
                    <div key={i} className="relative group">
                      {component || (
                        <button
                          onClick={onClick}
                          className="h-8 w-8 md:h-9 md:w-9 flex items-center justify-center rounded-sm border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors"
                          aria-label={label}>
                          {icon}
                        </button>
                      )}
                      {!component && (
                        <span className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:flex text-xs bg-black text-white px-2 py-1 whitespace-nowrap">
                          {label}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="text-right min-w-[140px] order-2 md:order-3 absolute md:relative top-4 right-4 md:top-auto md:right-auto">
          <h2 className="text-xs md:text-sm text-gray-500">
            {format(new Date(), "MMMM d, yyyy")}
          </h2>
          <p className="text-base md:text-lg font-semibold text-gray-900">
            {currentTime.toLocaleTimeString()}
          </p>
        </div>
      </div>

      {isModalOpen && (
        <ProfilePictureModal userId={user._id} onClose={handleCloseModal} />
      )}
    </div>
  );
}

export default UserBanner;
